import Tooltip from '../../common/Tooltip'
import Box from '@mui/material/Box'
import { TableConfig } from '../../common/Table'
import { AdBreak, AdBreakStatus, ListAdBreakSortableField } from 'common/api/v1/types'
import Circle from '@mui/icons-material/FiberManualRecord'
import HelpOutline from '@mui/icons-material/HelpOutline'
import { Theme } from '@mui/material/styles'
import { AppDispatch } from '../../../store'
import { ActionMenu } from './ActionMenu'
import { format } from 'date-fns'
import { DATE_FORMAT_LONG, DATE_FORMAT_SHORT } from 'common/api/v1/helpers'
import Typography from '@mui/material/Typography'
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined'
import { getDuration, getDurationFormatted, getEndTime, getEndTimeFormatted, getStartTimeFormatted } from './utils'
import CircularProgress from '@mui/material/CircularProgress'
import PlayArrow from '@mui/icons-material/PlayArrow'
import CircleOutlined from '@mui/icons-material/FiberManualRecordOutlined'

type AdBreakColumn = TableConfig<AdBreak>[number]

const styles = {
  inlineWrapper: {
    display: 'flex',
    alignItems: 'center',
    '& > span': {
      marginLeft: (theme: Theme) => theme.spacing(1),
    },
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '34px',
    height: '34px',
    pointerEvents: 'all',
    '& svg': {
      display: 'block',
    },
  },
  warning: {
    color: (theme: Theme) => theme.palette.warning.main,
  },
  error: {
    color: (theme: Theme) => theme.palette.error.main,
  },
  success: {
    color: (theme: Theme) => theme.palette.success.main,
  },
}

export const adBreakStatusText = (status: AdBreakStatus, errorMessage?: string) => {
  switch (status) {
    case AdBreakStatus.pending:
      return 'Waiting for Ad break to start'
    case AdBreakStatus.playing:
      return 'Ad break is playing'
    case AdBreakStatus.playOk:
      return 'Ad break finished successfully'
    case AdBreakStatus.playFailed:
      return errorMessage || 'Ad break finished with errors'
    case AdBreakStatus.collectingStats:
      return 'Ad break finished, collecting stats'
    default:
      return 'Unknown'
  }
}

export const AdBreakStatusIcon = ({ status }: { status: AdBreakStatus }) => {
  switch (status) {
    case AdBreakStatus.pending:
      return <PendingOutlinedIcon sx={styles.success} />
    case AdBreakStatus.playing:
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '& svg': {
              display: 'block',
            },
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress size={24} color="success" />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <PlayArrow fontSize="small" color="success" />
            </Box>
          </Box>
        </Box>
      )
    case AdBreakStatus.playOk:
      return <Circle sx={styles.success} />
    case AdBreakStatus.playFailed:
      return <Circle sx={styles.error} />
    case AdBreakStatus.collectingStats:
      return <CircleOutlined sx={styles.success} />
    default:
      return <HelpOutline sx={styles.warning} />
  }
}

const status = (): AdBreakColumn => ({
  title: 'status',
  getValue: ({ status, errorMessage }) => (
    <Tooltip title={adBreakStatusText(status, errorMessage)} placement="top">
      <Box sx={styles.container}>
        <div>
          <AdBreakStatusIcon status={status} />
        </div>
      </Box>
    </Tooltip>
  ),
  props: { noTypography: true },
})

const duration = (): AdBreakColumn => ({
  title: 'duration',
  getValue: (adBreak) => {
    const { duration } = getDuration(adBreak)
    return (
      <Tooltip title={`${duration} ms`} placement="top">
        <Box sx={styles.inlineWrapper}>
          <Typography variant="body2">{getDurationFormatted(adBreak)}</Typography>
        </Box>
      </Tooltip>
    )
  },
})

const createdAt = (): AdBreakColumn => ({
  title: 'created',
  getValue: ({ createdAt }) => (
    <Tooltip title={format(new Date(createdAt), DATE_FORMAT_LONG)} placement="top">
      <Typography variant="body2">{format(new Date(createdAt), DATE_FORMAT_SHORT)}</Typography>
    </Tooltip>
  ),
  sorting: {
    byParameter: ListAdBreakSortableField.createdAt,
  },
})

export interface AdBreakTableConfig {
  dispatch: AppDispatch
}

export const getTableConfig = ({ dispatch }: AdBreakTableConfig): AdBreakColumn[] => [
  status(),
  duration(),
  {
    title: 'start time',
    getValue: (adBreak) => {
      return (
        <Tooltip title={format(adBreak.startsAt, DATE_FORMAT_LONG)} placement="top">
          <Box>{getStartTimeFormatted(adBreak)}</Box>
        </Tooltip>
      )
    },
    sorting: {
      byParameter: ListAdBreakSortableField.startsAt,
    },
  },
  {
    title: 'end time',
    getValue: (adBreak) => {
      const { timestamp } = getEndTime(adBreak)
      return (
        <Tooltip title={timestamp ? format(timestamp, DATE_FORMAT_LONG) : ''} placement="top">
          <Box>{getEndTimeFormatted(adBreak)}</Box>
        </Tooltip>
      )
    },
  },
  createdAt(),
  {
    title: '',
    getValue: (adBreak: AdBreak) => <ActionMenu adBreak={adBreak} dispatch={dispatch} />,
    props: { noTypography: true },
  },
]

export const getSmallTableConfig = (): AdBreakColumn[] => [
  status(),
  duration(),
  {
    title: 'start time',
    getValue: (adBreak) => {
      return (
        <Tooltip title={format(adBreak.startsAt, DATE_FORMAT_LONG)} placement="top">
          <Box>{getStartTimeFormatted(adBreak)}</Box>
        </Tooltip>
      )
    },
    sorting: {
      byParameter: ListAdBreakSortableField.startsAt,
    },
  },
  {
    title: 'end time',
    getValue: (adBreak) => {
      const { timestamp } = getEndTime(adBreak)
      return (
        <Tooltip title={timestamp ? format(timestamp, DATE_FORMAT_LONG) : ''} placement="top">
          <Box>{getEndTimeFormatted(adBreak)}</Box>
        </Tooltip>
      )
    },
  },
]
